import DOMPurify from 'dompurify';

/**
 * Sanitize HTML content for safe rendering.
 * Used for document content, governance documents, and user-generated HTML.
 */
export function sanitizeHtml(html: string | null | undefined): string {
  if (!html) return '';
  return DOMPurify.sanitize(html);
}

/**
 * Sanitize video embed code with strict whitelist.
 * Only allows iframes from trusted video platforms (YouTube, Vimeo).
 */
export function sanitizeVideoEmbed(html: string | null | undefined): string {
  if (!html) return '';
  
  // Configure DOMPurify for video embeds only
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS: ['iframe'],
    ALLOWED_ATTR: ['src', 'width', 'height', 'frameborder', 'allow', 'allowfullscreen', 'title', 'class', 'style'],
    // Only allow YouTube and Vimeo embeds
    ALLOWED_URI_REGEXP: /^https:\/\/(www\.)?(youtube\.com|youtube-nocookie\.com|youtu\.be|vimeo\.com|player\.vimeo\.com)\//i,
  });
}

/**
 * Safe URI scheme regex for href/src attributes in document content.
 * Allows: http, https, mailto, tel, relative URLs, and fragment identifiers.
 * Blocks: javascript:, data:, vbscript:, file:, and all other schemes.
 */
const SAFE_URI_REGEXP = /^(?:(?:https?|mailto|tel):|[^a-z]|[a-z+.\-]+(?:[^a-z+.\-:]|$))/i;

// Register the target/rel hook once at module load to avoid duplicate
// registration. Forces rel="noopener noreferrer" on any <a> that has a target
// attribute (e.g. target="_blank"), preventing tabnabbing / referrer leaks.
DOMPurify.addHook('afterSanitizeAttributes', (node) => {
  if (node.tagName === 'A' && node.hasAttribute('target')) {
    node.setAttribute('rel', 'noopener noreferrer');
  }
});

/**
 * Sanitize document content that may contain formatting and signature blocks.
 * Preserves common HTML formatting while preventing script injection.
 */
export function sanitizeDocumentContent(html: string | null | undefined): string {
  if (!html) return '';
  
  return DOMPurify.sanitize(html, {
    // Allow common document formatting tags
    ALLOWED_TAGS: [
      'p', 'br', 'div', 'span', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
      'strong', 'b', 'em', 'i', 'u', 's', 'strike', 'sub', 'sup',
      'ul', 'ol', 'li', 'blockquote', 'pre', 'code',
      'table', 'thead', 'tbody', 'tr', 'th', 'td',
      'a', 'img', 'hr',
      // Signature block elements
      'canvas',
      // NOTE: <style> is a residual XSS risk (CSS can exfiltrate data via
      // background-image URLs and similar tricks) and is a candidate for
      // removal once ecclesiastical document formatting no longer relies on
      // inline <style> blocks.
      'style'
    ],
    ALLOWED_ATTR: [
      'class', 'style', 'id', 'data-*',
      'href', 'target', 'rel',
      'src', 'alt', 'width', 'height',
      'colspan', 'rowspan',
    ],
    // Restrict href/src to safe URI schemes; blocks javascript:, data:, etc.
    ALLOWED_URI_REGEXP: SAFE_URI_REGEXP,
    // Allow data attributes for document structure
    ALLOW_DATA_ATTR: true,
  });
}
